import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { formatZonedDateTimeLine, isIsoUtc } from '../../../features/pcheck/helpers/booking-timezone';

export interface MeetingDetailRow {
  label: string;
  value: string;
  copyable?: boolean;
}

@Component({
  selector: 'app-meeting-detail-table',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './meeting-detail-table.component.html',
  styleUrls: ['./meeting-detail-table.component.scss'],
})
export class MeetingDetailTableComponent {
  /** Account name row. */
  @Input() accountName?: string;
  /** Executive / rep name row. */
  @Input() executive?: string;
  /** Meeting subject row. */
  @Input() subject?: string;
  /** ISO-8601 UTC start time — displayed as formatted string. */
  @Input() startTime?: string;
  /** ISO-8601 UTC end time — displayed as formatted string. */
  @Input() endTime?: string;
  /** Primary attendee email row. */
  @Input() attendeeEmail?: string;
  /** Additional guest emails — rendered as stacked lines. */
  @Input() additionalGuests?: string[];
  /**
   * All emails already on the invite — used in the Add Participants card
   * to show who is already invited so the user avoids duplicates.
   */
  @Input() alreadyInvited?: string[];
  /** Show copy-to-clipboard buttons next to each value. */
  @Input() showCopyButtons = false;
  /**
   * IANA timezone to render startTime/endTime in. When set AND the time is a
   * UTC ISO-8601 instant, the value is formatted in this zone. When unset (or the
   * value is a pre-formatted string, e.g. legacy IMPACT payloads), the raw value
   * is shown verbatim.
   */
  @Input() displayTimezone?: string;

  /** Emitted when the user clicks a copy button. */
  @Output() copyValue = new EventEmitter<{ value: string; label: string }>();

  /** Format a time value for display: zoned when ISO + tz known, else raw. */
  displayTime(value?: string): string {
    if (!value) return '';
    if (this.displayTimezone && isIsoUtc(value)) {
      return formatZonedDateTimeLine(value, this.displayTimezone);
    }
    return value;
  }

  onCopy(value: string, label: string): void {
    navigator.clipboard.writeText(value).catch(() => {});
    this.copyValue.emit({ value, label });
  }
}
